import { JDOODLEBLOCKSNAMES } from '@/utils/embeds/editorMeta'
import { type IMeta } from '@/utils/meta'

import executeService from '@/services/embeds/plugin/execute.service'

import {
  useEmbedConfigureStore,
  type IComponentSettings,
  type IEmbedLanguage
} from '@/stores/embeds/embedConfigure.store'
import { useIdeStore } from '@/stores/embeds/ide.store'

let editProgramViewSelectedLanguage: IEmbedLanguage | null = null

/**
 * Get the script
 * @returns The script
 */
const getScript = () => {
  if (useIdeStore().codeEditor) {
    return codeEditorGetProgram()
  } else {
    let selectedLanguageScript: string = ''
    useEmbedConfigureStore().getEmbedConfig?.languages.forEach((language) => {
      if (language.language === useIdeStore().selectedLanguage?.language) {
        selectedLanguageScript = language.program as string
      }
    })
    return JSON.parse(selectedLanguageScript) || ''
  }
}
/**
 * Get the program
 * @returns The program
 */
const codeEditorGetProgram = () => {
  if (!useIdeStore().codeEditor) return ''
  return useIdeStore().codeEditor.getSession().getValue()
}
/**
 * Set the program
 * @param program - The program to set
 */
const codeEditorSetProgram = (program: string) => {
  if (!useIdeStore().codeEditor) return
  useIdeStore().codeEditor.getSession().setValue(program)
  heightChangeFunction()
}
/**
 * Update which language is selected in the edit program view
 * @param language - The language to set
 */
const setEditProgramViewSelectedLanguage = (language: IEmbedLanguage) => {
  editProgramViewSelectedLanguage = language
  const program = language.program || null
  if (program) codeEditorSetProgram(JSON.parse(program))
  else codeEditorSetProgram('')
}

/**
 * write the program to the draft
 */
const saveProgramToDraft = () => {
  if (!editProgramViewSelectedLanguage) return
  editProgramViewSelectedLanguage.program = JSON.stringify(
    useIdeStore().codeEditor?.getSession().getValue()
  )
}
/**
 * Sets font size of code editor
 */
const codeEditorSetFontSize = () => {
  if (!useIdeStore().codeEditor) return
  const fontSize = useIdeStore().fontSize
    ? useIdeStore().fontSize
    : useEmbedConfigureStore().getEmbedConfig?.defaultConfig?.fontSize || 12

  useIdeStore().codeEditor.setOptions({
    fontSize: `${fontSize}px`
  })
}
/**
 * On height change function
 */
const heightChangeFunction = () => {
  const codeEditorRef = useIdeStore().codeEditorRef
  const editor = useIdeStore().codeEditor

  if (!editor || !codeEditorRef) return
  const minHeight = 384
  if (useIdeStore().onEditThemeView || useIdeStore().onEditView) {
    codeEditorRef.style.setProperty('height', '100%')
    codeEditorRef.style.setProperty('min-height', `${minHeight}px`)
    editor.resize()
    return
  }
  let lineHeight =
    editor.getSession().getScreenLength() * editor.renderer.lineHeight +
    editor.renderer.scrollBar.getWidth() +
    20
  if (lineHeight < minHeight) {
    lineHeight = minHeight
  }
  const modelHeight = useIdeStore().codeEditorModal
    ? parseInt(useIdeStore().codeEditorModal.getStyle().height)
    : minHeight
  const settongs = useEmbedConfigureStore().getComponentSettings?.find(
    (componentSetting: IComponentSettings) =>
      componentSetting.componentName === JDOODLEBLOCKSNAMES.IDECOMP
  )?.configStyle
  const autoGrow = settongs?.find((setting) => setting.id === 'autoGrow')?.value === 'true' || false

  const componentHeight = document.getElementById(JDOODLEBLOCKSNAMES.IDECOMP) || null
  if (autoGrow) {
    if (useIdeStore().codeEditorModal) {
      if (lineHeight !== modelHeight) {
        useIdeStore().codeEditorModal.setStyle({
          ...useIdeStore().codeEditorModal.getStyle(),
          height: `${lineHeight}px`
        })
        codeEditorRef.style.setProperty('height', `${lineHeight}px`)
      }
    } else if (componentHeight) {
      const parentComponent = componentHeight.parentElement as HTMLElement
      if (parentComponent) parentComponent.style.setProperty('height', `${lineHeight}px`)
      componentHeight.style.setProperty('height', `${lineHeight}px`)
      codeEditorRef.style.setProperty('height', `${lineHeight}px`)
    }
  } else {
    if (useIdeStore().codeEditorModal) {
      if (modelHeight < minHeight) {
        useIdeStore().codeEditorModal.setStyle({
          ...useIdeStore().codeEditorModal.getStyle(),
          height: `${minHeight}px`
        })
        codeEditorRef.style.setProperty('height', `${modelHeight}px`)
      } else if (lineHeight != modelHeight) {
        codeEditorRef.style.setProperty('height', `${modelHeight}px`)
      }
    } else {
      if (componentHeight) {
        const parentComponentHeight = componentHeight.parentElement?.clientHeight || minHeight
        componentHeight.style.setProperty('height', `${parentComponentHeight}px`)
        codeEditorRef.style.setProperty('height', `${parentComponentHeight}px`)
      }
    }
  }

  codeEditorRef.style.setProperty('min-height', `${minHeight}px`)
  editor.resize()
}

/**
 * On keyup code editor
 */
const onKeyupCodeEditor = () => {
  useIdeStore()
    .codeEditor.textInput.getElement()
    .addEventListener('keyup', (event: KeyboardEvent) => {
      heightChangeFunction()
      if (useIdeStore().onEditView) saveProgramToDraft()
      // Ctrl + Enter to execute code
      if (event.ctrlKey && event.key === 'Enter') {
        executeService.tryExecute()
      }
    })
}
/**
 * Set code editors set auto completion
 * @param value - The value to set
 */
const codeEditorSetLiveAutocompletion = (value: boolean) => {
  if (!useIdeStore().codeEditor) return
  useIdeStore().codeEditor.setOptions({
    enableBasicAutocompletion: value,
    enableLiveAutocompletion: value
  })
}
/**
 * Set code editors gutter
 * @param value - The value to set
 */
const codeEditorSetShowGutter = (value: boolean) => {
  if (!useIdeStore().codeEditor) return
  useIdeStore().codeEditor.renderer.setShowGutter(value)
}
/**
 * Set code editors read only
 * @param value - The value to set
 */
const codeEditorSetReadOnly = (value: boolean) => {
  if (!useIdeStore().codeEditor) return
  useIdeStore().codeEditor.setReadOnly(useIdeStore().onEditView ? false : value)
}
/**
 * Set code editor theme
 * @param theme - The theme to set
 */
const codeEditorSetTheme = (theme: string) => {
  if (!useIdeStore().codeEditor) return

  useIdeStore().codeEditor.setTheme(`ace/theme/${theme}`)
}
/**
 * Set code editor language
 * @param language - The language to set
 */
const codeEditorSetLanguage = (language: string) => {
  if (!useIdeStore().codeEditor) return
  const meta: IMeta = useEmbedConfigureStore().getLanguageMeta[language as keyof IMeta] as IMeta
  const aceCode = meta?.aceCode || 'empty'
  useIdeStore().codeEditor.getSession().setMode(`ace/mode/${aceCode}`)
  if (language === 'whitespace') {
    useIdeStore().codeEditor.setShowInvisibles(true)
  } else {
    useIdeStore().codeEditor.setShowInvisibles(false)
  }
}
/**
 * Initialize the ace editor
 * @param codeEditor - The codeEditor element to initialize
 * @returns The ace editor
 */
const initAceEditor = (codeEditor: Element) => {
  window.ace.require('ace/ext/language_tools')
  window.ace.config.set('basePath', '/assets/javascript/ace')

  const editor: ace['Editor'] = window.ace.edit(codeEditor)
  editor.renderer.attachToShadowRoot()
  editor.renderer.setAnimatedScroll(true)
  editor.renderer.setShowPrintMargin(false)

  editor.setOptions({
    enableSnippets: true
  })
  editor.$blockScrolling = Infinity

  return editor
}
/**
 * Initialize the editor when router changes
 * @param element - The element to initialize
 * @param count - The count of the init
 * @returns null
 */
const initCodeEditor = async (element: HTMLElement, count: number = 0) => {
  if (!useIdeStore().isWindowAce()) {
    if (count > 10) {
      useIdeStore().setShareNotFound(true)
      useIdeStore().setShareNotFoundHttpError('Unable to load the Embeds. Please try again later.')
    } else {
      await new Promise((resolve) => setTimeout(resolve, 100))
      initCodeEditor(element, count + 1)
    }
  } else {
    if (useIdeStore().codeEditor) {
      destroyCodeEditor()
    }
    useIdeStore().codeEditorRef = element
    useIdeStore().codeEditor = initAceEditor(element)
    onSettingChange()

    const aceMode = useEmbedConfigureStore().getEmbedConfig?.languages[0].language || 'java'
    codeEditorSetLanguage(aceMode)
    const theme = useEmbedConfigureStore().getEmbedConfig?.languages[0].theme || 'xcode'
    codeEditorSetTheme(theme)
    codeEditorSetFontSize()
    const program = useEmbedConfigureStore().getEmbedConfig?.languages[0].program || null
    if (program) codeEditorSetProgram(JSON.parse(program))
    await new Promise((resolve) => setTimeout(resolve, 100))
    heightChangeFunction()
    onKeyupCodeEditor()
  }
}
/**
 * On setting change
 */
const onSettingChange = () => {
  if (!useIdeStore().codeEditor) return
  const settongs = useEmbedConfigureStore().getComponentSettings?.find(
    (componentSetting: IComponentSettings) =>
      componentSetting.componentName === JDOODLEBLOCKSNAMES.IDECOMP
  )?.configStyle
  const readOnly = settongs?.find((setting) => setting.id === 'readOnly')?.value === 'true' || false
  codeEditorSetReadOnly(readOnly)
  const lineNumber =
    settongs?.find((setting) => setting.id === 'lineNumber')?.value === 'true' || false
  codeEditorSetShowGutter(lineNumber)
  const autoCompletion =
    settongs?.find((setting) => setting.id === 'autoCompletion')?.value === 'true' || false
  codeEditorSetLiveAutocompletion(autoCompletion)
  heightChangeFunction()
}
/**
 * Set the code editor modal
 * @param modal - The modal to set
 */
const setModal = (modal: any) => {
  useIdeStore().codeEditorModal = modal
}
/**
 * Destroy the code editor
 */
const destroyCodeEditor = () => {
  if (!useIdeStore().codeEditor) return
  useIdeStore().codeEditor.destroy()
  useIdeStore().codeEditor = null
  useIdeStore().codeEditorRef = null
  useIdeStore().codeEditorModal = null
}

export default {
  getScript,
  codeEditorGetProgram,
  codeEditorSetFontSize,
  setModal,
  initCodeEditor,
  codeEditorSetLanguage,
  codeEditorSetTheme,
  codeEditorSetProgram,
  onSettingChange,
  heightChangeFunction,
  setEditProgramViewSelectedLanguage,
  destroyCodeEditor
}
